import { useState } from "react"
import { Plus } from "@phosphor-icons/react"
import { useUser } from "@/apis/bff"
import { Button } from "@/components/ui/button"
import { List } from "@/components/ui/list"
import { ChResendDialog } from "@/modules/patients/detail/conclude/ch-resend-dialog"
import { useEditMode } from "@/modules/patients/detail/hooks/useEditMode"
import { ConsultationReportDocument } from "@/modules/patients/detail/progress-note/documents/consultation-report-document"
import { GeneralAttestationDocument } from "@/modules/patients/detail/progress-note/documents/general-attestation-document"
import { LaboratoryPrescriptionDocument } from "@/modules/patients/detail/progress-note/documents/laboratory-prescription-document"
import { LegalGuardianAttestation } from "@/modules/patients/detail/progress-note/documents/legal-guardian-attestation"
import { MeasLaboratoryPrescriptionDocument } from "@/modules/patients/detail/progress-note/documents/meas-laboratory-prescription-document"
import { MeasRadiologyPrescriptionDocument } from "@/modules/patients/detail/progress-note/documents/meas-radiology-prescription-document"
import { MedicalReportAttachments } from "@/modules/patients/detail/progress-note/documents/medical-report-attachments"
import { NeuropsychologicDiagnosticsPrescriptionDocument } from "@/modules/patients/detail/progress-note/documents/neuropsychologic-diagnostic-prescription-document"
import { NutritionalCounsellingDocument } from "@/modules/patients/detail/progress-note/documents/nutritional-counselling-document"
import { PhysiotherapyDocument } from "@/modules/patients/detail/progress-note/documents/physiotherapy-document"
import { PrescriptionDocument } from "@/modules/patients/detail/progress-note/documents/prescription-document"
import { PsychologicalPsychoTherapy } from "@/modules/patients/detail/progress-note/documents/psychological-psychotherapy"
import ReferralLetterDocument from "@/modules/patients/detail/progress-note/documents/referral-letter-document"
import { SickLeaveCertificateDocument } from "@/modules/patients/detail/progress-note/documents/sick-leave-document"
import { TherapyPrescriptionDocument } from "@/modules/patients/detail/progress-note/documents/therapy-prescription-document"
import { TreatmentPlanDocument } from "@/modules/patients/detail/progress-note/documents/treatment-plan-document"
import { ProgressNoteData } from "@/modules/patients/detail/progress-note/types/progress-note-fragment-type"
import {
  formatProgressNoteDocuments,
  SelectedDocumentForViewingType,
} from "@/modules/patients/detail/store/useDocumentStore"
import { useAppointmentParam } from "@/modules/patients/hooks/useAppointmentParam"
import { useCountryParam } from "@/modules/patients/hooks/useCountryParam"
import { isConcludedCarePlanDocument } from "@/modules/patients/libs/is-concluded-care-plan-document"
import { ChProgressNoteDocument, DeProgressNoteDocument } from "@/modules/patients/types/ProgressNoteDocument"
import { UploadDialog } from "@/modules/upload/upload-dialog"

interface ReadOnlyDocumentListProps {
  progressNote: ProgressNoteData
  onSelect: ({ type, fileId }: SelectedDocumentForViewingType) => void
}

const DocumentListReadOnlySection = ({ progressNote, onSelect }: ReadOnlyDocumentListProps) => {
  const appointmentId = useAppointmentParam()
  const countryCode = useCountryParam()
  const isEditMode = useEditMode()
  const [isUploadOpen, setIsUploadOpen] = useState(false)
  const [isResendReferralOpen, setIsResendReferralOpen] = useState(false)
  const { role } = useUser()

  const { id: progressNoteId } = progressNote

  const isMedSupOrCSC = role === "MedicalSupervisor" || role === "CustomerServiceCenter"
  const chReferralLetterRef =
    countryCode === "CH"
      ? (formatProgressNoteDocuments(progressNote, false) as ChProgressNoteDocument).referralLetter
      : null

  const getDocumentsByCountry = () => {
    switch (countryCode) {
      case "CH": {
        const {
          carePlanSummary,
          prescription,
          sickLeave,
          attestationGuardian,
          physiotherapy,
          ch_nutritionalPrescription,
          psychologicalPsychotherapy,
          generalAttestation,
          referralLetter,
          ch_measRadiologyPrescription,
          measLaboratoryPrescription,
          ch_neuropsychologicDiagnosticPrescription,
          ch_laboratoryPrescription,
          ch_consultationReport,
        } = formatProgressNoteDocuments(progressNote, false) as ChProgressNoteDocument
        return (
          <>
            {isConcludedCarePlanDocument(carePlanSummary) && (
              <TreatmentPlanDocument
                key={carePlanSummary.id}
                id={carePlanSummary.id}
                treatmentPlan={carePlanSummary.treatmentPlan}
                isEditable={false}
                isSigned={!!carePlanSummary.signedAt}
                isSent={!!(carePlanSummary.concludedAt && carePlanSummary.concludedBy)}
                onSelect={() => onSelect({ ...carePlanSummary })}
              />
            )}
            {prescription && (
              <PrescriptionDocument
                key={prescription.id}
                id={prescription.id}
                prescriptionRef={prescription}
                healthcareProviderId={prescription.healthcareProviderId}
                isSigned={!!prescription.signedAt}
                progressNoteId={progressNoteId}
                medicationIds={prescription.medications.map((i) => i.id)}
                isEditable={false}
                isSent={!!(prescription.sentAt && prescription.sentBy)}
                onSelect={() => onSelect({ ...prescription })}
              />
            )}
            {sickLeave && (
              <SickLeaveCertificateDocument
                key={sickLeave.id}
                sickLeaveRef={sickLeave}
                isSigned={!!sickLeave.signedAt}
                progressNoteId={progressNoteId}
                isEditable={false}
                isSent={!!(sickLeave.sentAt && sickLeave.sentBy)}
                onSelect={() => onSelect({ ...sickLeave })}
              />
            )}
            {attestationGuardian && (
              <LegalGuardianAttestation
                key={attestationGuardian.id}
                attestationGuardianRef={attestationGuardian}
                isSigned={!!attestationGuardian.signedAt}
                isEditable={false}
                isSent={!!(attestationGuardian.sentAt && attestationGuardian.sentBy)}
                onSelect={() => onSelect({ ...attestationGuardian })}
              />
            )}
            {physiotherapy && (
              <PhysiotherapyDocument
                key={physiotherapy.id}
                id={physiotherapy.id}
                physiotherapyRef={physiotherapy}
                isSigned={!!physiotherapy.signedAt}
                progressNoteId={progressNoteId}
                isEditable={false}
                healthcareProviderId={physiotherapy.healthcareProviderId ?? ""}
                treatmentUnit={physiotherapy.treatmentUnit}
                isSent={!!(physiotherapy.sentAt && physiotherapy.sentBy)}
                onSelect={() => onSelect({ ...physiotherapy })}
              />
            )}
            {ch_nutritionalPrescription && (
              <NutritionalCounsellingDocument
                key={ch_nutritionalPrescription.id}
                nutritionalCounsellingRef={ch_nutritionalPrescription}
                isSigned={!!ch_nutritionalPrescription.signedAt}
                progressNoteId={progressNoteId}
                isEditable={false}
                healthcareProviderId={ch_nutritionalPrescription.healthcareProviderId ?? ""}
                isSent={!!(ch_nutritionalPrescription.sentAt && ch_nutritionalPrescription.sentBy)}
                onSelect={() => onSelect({ ...ch_nutritionalPrescription })}
              />
            )}
            {psychologicalPsychotherapy && (
              <PsychologicalPsychoTherapy
                key={psychologicalPsychotherapy.id}
                psychologicalPsychoTherapyRef={psychologicalPsychotherapy}
                isSigned={!!psychologicalPsychotherapy.signedAt}
                progressNoteId={progressNoteId}
                isEditable={false}
                isSent={!!(psychologicalPsychotherapy.sentAt && psychologicalPsychotherapy.sentBy)}
                onSelect={() => onSelect({ ...psychologicalPsychotherapy })}
              />
            )}
            {generalAttestation && (
              <GeneralAttestationDocument
                key={generalAttestation.id}
                generalAttestationRef={generalAttestation}
                isEditable={false}
                isSigned={!!generalAttestation.signedAt}
                isSent={!!(generalAttestation.sentAt && generalAttestation.sentBy)}
                onSelect={() => onSelect({ ...generalAttestation })}
              />
            )}
            {referralLetter && (
              <ReferralLetterDocument
                key={referralLetter.id}
                fragmentRef={referralLetter}
                progressNoteId={progressNoteId}
                isEditable={false}
                isSigned={!!referralLetter.signedAt}
                isSent={!!(referralLetter.concludedAt && referralLetter.concludedBy)}
                onSelect={() => onSelect({ ...referralLetter })}
                onResend={isMedSupOrCSC && isEditMode ? () => setIsResendReferralOpen(true) : undefined}
              />
            )}
            {measLaboratoryPrescription && (
              <MeasLaboratoryPrescriptionDocument
                key={measLaboratoryPrescription.id}
                id={measLaboratoryPrescription.id}
                measLaboratoryPrescriptionRef={measLaboratoryPrescription}
                isSigned={!!measLaboratoryPrescription.signedAt}
                progressNoteId={progressNoteId}
                isEditable={false}
                isSent={!!(measLaboratoryPrescription.sentAt && measLaboratoryPrescription.sentBy)}
                onSelect={() => onSelect({ ...measLaboratoryPrescription })}
              />
            )}
            {ch_measRadiologyPrescription && (
              <MeasRadiologyPrescriptionDocument
                key={ch_measRadiologyPrescription.id}
                id={ch_measRadiologyPrescription.id}
                measRadiologyPrescriptionRef={ch_measRadiologyPrescription}
                progressNoteId={progressNoteId}
                isEditable={false}
                isSigned={!!ch_measRadiologyPrescription.signedAt}
                isSent={!!(ch_measRadiologyPrescription.sentAt && ch_measRadiologyPrescription.sentBy)}
                onSelect={() => onSelect({ ...ch_measRadiologyPrescription })}
              />
            )}
            {ch_neuropsychologicDiagnosticPrescription && (
              <NeuropsychologicDiagnosticsPrescriptionDocument
                key={ch_neuropsychologicDiagnosticPrescription.id}
                id={ch_neuropsychologicDiagnosticPrescription.id}
                neuropsychologicDiagnosticPrescriptionRef={ch_neuropsychologicDiagnosticPrescription}
                progressNoteId={progressNoteId}
                isEditable={false}
                isSigned={!!ch_neuropsychologicDiagnosticPrescription.signedAt}
                isSent={
                  !!(
                    ch_neuropsychologicDiagnosticPrescription.sentAt && ch_neuropsychologicDiagnosticPrescription.sentBy
                  )
                }
                onSelect={() => onSelect({ ...ch_neuropsychologicDiagnosticPrescription })}
              />
            )}
            {ch_laboratoryPrescription && (
              <LaboratoryPrescriptionDocument
                key={ch_laboratoryPrescription.id}
                id={ch_laboratoryPrescription.id}
                laboratoryPrescriptionRef={ch_laboratoryPrescription}
                progressNoteId={progressNoteId}
                isEditable={false}
                isSigned={!!ch_laboratoryPrescription.signedAt}
                isSent={!!(ch_laboratoryPrescription.sentAt && ch_laboratoryPrescription.sentBy)}
                onSelect={() => onSelect({ ...ch_laboratoryPrescription })}
              />
            )}
            {ch_consultationReport && (
              <ConsultationReportDocument
                key={ch_consultationReport.id}
                fragmentRef={ch_consultationReport}
                progressNoteId={progressNoteId}
                isEditable={false}
                isSigned={!!ch_consultationReport.signedAt}
                isSent={!!(ch_consultationReport.sentAt && ch_consultationReport.sentBy)}
                onSelect={() => onSelect({ ...ch_consultationReport })}
              />
            )}
          </>
        )
      }

      case "DE": {
        const {
          carePlanSummary,
          prescription,
          attestationGuardian,
          sickLeave,
          therapyPrescription,
          generalAttestation,
          referralLetter,
        } = formatProgressNoteDocuments(progressNote, false) as DeProgressNoteDocument

        return (
          <>
            {isConcludedCarePlanDocument(carePlanSummary) && (
              <TreatmentPlanDocument
                key={carePlanSummary.id}
                id={carePlanSummary.id}
                treatmentPlan={carePlanSummary.treatmentPlan}
                isEditable={false}
                isSigned={!!carePlanSummary.signedAt}
                isSent={!!(carePlanSummary.concludedAt && carePlanSummary.concludedBy)}
                onSelect={() => onSelect({ ...carePlanSummary })}
              />
            )}
            {prescription && (
              <PrescriptionDocument
                key={prescription.id}
                prescriptionRef={prescription}
                progressNoteId={progressNoteId}
                medicationIds={prescription.medications.map((i) => i.id)}
                isEditable={false}
                isSent={!!(prescription.concludedAt && prescription.concludedBy)}
                onSelect={() => onSelect({ ...prescription })}
              />
            )}
            {attestationGuardian && (
              <LegalGuardianAttestation
                key={attestationGuardian.id}
                attestationGuardianRef={attestationGuardian}
                isEditable={false}
                isSent={!!(attestationGuardian.concludedAt && attestationGuardian.concludedBy)}
                onSelect={() => onSelect({ ...attestationGuardian })}
              />
            )}
            {sickLeave && (
              <SickLeaveCertificateDocument
                key={sickLeave.id}
                sickLeaveRef={sickLeave}
                progressNoteId={progressNoteId}
                isEditable={false}
                isSent={!!(sickLeave.concludedAt && sickLeave.concludedBy)}
                onSelect={() => onSelect({ ...sickLeave })}
              />
            )}
            {therapyPrescription && (
              <TherapyPrescriptionDocument
                key={therapyPrescription.id}
                therapyPrescriptionRef={therapyPrescription}
                progressNoteId={progressNoteId}
                isEditable={false}
                isSent={!!(therapyPrescription.concludedAt && therapyPrescription.concludedBy)}
                onSelect={() => onSelect({ ...therapyPrescription })}
              />
            )}
            {generalAttestation && (
              <GeneralAttestationDocument
                key={generalAttestation.id}
                generalAttestationRef={generalAttestation}
                isEditable={false}
                isSent={!!(generalAttestation.concludedAt && generalAttestation.concludedBy)}
                onSelect={() => onSelect({ ...generalAttestation })}
              />
            )}
            {referralLetter && (
              <ReferralLetterDocument
                key={referralLetter.id}
                fragmentRef={referralLetter}
                progressNoteId={progressNoteId}
                isEditable={false}
                isSent={!!(referralLetter.concludedAt && referralLetter.concludedBy)}
                onSelect={() => onSelect({ ...referralLetter })}
              />
            )}
          </>
        )
      }
    }
  }

  return (
    <>
      <List className="grid gap-2">
        {isMedSupOrCSC && isEditMode && !appointmentId && (
          <div className="flex justify-end">
            <Button variant="tertiary" className="flex h-10 w-10 justify-end p-2" onClick={() => setIsUploadOpen(true)}>
              <Plus className="text-on-surface-subdued" />
            </Button>
          </div>
        )}
        {getDocumentsByCountry()}
        <MedicalReportAttachments
          medicalReportAttachments={progressNote.medicalReportAttachments}
          isEditMode={isEditMode}
        />
      </List>
      {isUploadOpen && (
        <UploadDialog country={countryCode} progressNoteId={progressNote.id} onOpenChange={setIsUploadOpen} />
      )}
      {isResendReferralOpen && chReferralLetterRef && (
        <ChResendDialog
          progressNote={progressNote}
          referralLetterRef={chReferralLetterRef}
          onClose={() => setIsResendReferralOpen(false)}
        />
      )}
    </>
  )
}

export { DocumentListReadOnlySection }
